import { c as _c } from "react/compiler-runtime";
import { IcoChevronDown16 } from '@onefootprint/icons';
import { cx } from 'class-variance-authority';
import Dropdown from '../../../../../dropdown';
import type { CompositeMode, SchemaPropertyType } from '../../../../utils/schema-model';
type TypeSelectorProps = {
  type: SchemaPropertyType;
  arrayItemType?: SchemaPropertyType;
  compositeMode?: CompositeMode;
  onChange: (type: SchemaPropertyType, arrayItemType?: SchemaPropertyType, compositeMode?: CompositeMode) => void;
  isDisabled?: boolean;
};
const TYPE_LABELS: Record<SchemaPropertyType | 'array', string> = {
  string: 'STR',
  number: 'NUM',
  integer: 'INT',
  boolean: 'BOOL',
  object: 'OBJ',
  array: 'ARR',
  enum: 'ENUM',
  composite: 'COMPOSITE',
  unknown: '???'
};
const COMPOSITE_MODE_LABELS: Record<CompositeMode, string> = {
  oneOf: 'ONEOF',
  anyOf: 'ANYOF',
  allOf: 'ALLOF'
};
const TYPES: SchemaPropertyType[] = ['string', 'number', 'integer', 'boolean', 'object', 'enum'];
const ARRAY_ITEM_TYPES: SchemaPropertyType[] = ['string', 'number', 'integer', 'boolean', 'object', 'enum'];
const COMPOSITE_MODES: CompositeMode[] = ['oneOf', 'anyOf', 'allOf'];
const TypeSelector = t0 => {
  const $ = _c(38);
  const {
    type,
    arrayItemType,
    compositeMode,
    onChange,
    isDisabled: t1
  } = t0;
  const isDisabled = t1 === undefined ? false : t1;
  let t2;
  if ($[0] !== arrayItemType || $[1] !== compositeMode || $[2] !== type) {
    t2 = () => {
      if (type === "array" && arrayItemType) {
        return `${TYPE_LABELS.array}<${TYPE_LABELS[arrayItemType] || arrayItemType}>`;
      }
      if (type === "composite" && compositeMode) {
        return COMPOSITE_MODE_LABELS[compositeMode];
      }
      return TYPE_LABELS[type];
    };
    $[0] = arrayItemType;
    $[1] = compositeMode;
    $[2] = type;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const getDisplayLabel = t2;
  const t3 = type === "unknown" ? "text-error border-error hover:border-error" : "text-secondary border-tertiary hover:border-primary";
  let t4;
  if ($[4] !== t3) {
    t4 = cx("flex h-full w-[128px] items-center justify-around text-caption-1 border rounded-md transition-colors disabled:opacity-50 disabled:hover:border-tertiary", t3);
    $[4] = t3;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== getDisplayLabel) {
    t5 = getDisplayLabel();
    $[6] = getDisplayLabel;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== t5) {
    t6 = <span>{t5}</span>;
    $[8] = t5;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <IcoChevronDown16 />;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] !== isDisabled || $[12] !== t4 || $[13] !== t6) {
    t8 = <button type="button" className={t4} disabled={isDisabled}>{t6}{t7}</button>;
    $[11] = isDisabled;
    $[12] = t4;
    $[13] = t6;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] !== isDisabled || $[16] !== t8) {
    t9 = <Dropdown.Trigger disabled={isDisabled}>{t8}</Dropdown.Trigger>;
    $[15] = isDisabled;
    $[16] = t8;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  let t10;
  if ($[18] !== onChange) {
    t10 = TYPES.map(t => <Dropdown.Item key={t} onSelect={() => onChange(t)}>{TYPE_LABELS[t]}</Dropdown.Item>);
    $[18] = onChange;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  let t11;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <Dropdown.Divider />;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  let t12;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <Dropdown.SubTrigger>Array of...</Dropdown.SubTrigger>;
    $[21] = t12;
  } else {
    t12 = $[21];
  }
  let t13;
  if ($[22] !== onChange) {
    t13 = ARRAY_ITEM_TYPES.map(t_0 => <Dropdown.Item key={t_0} onSelect={() => onChange("array", t_0)}>{TYPE_LABELS[t_0]}</Dropdown.Item>);
    $[22] = onChange;
    $[23] = t13;
  } else {
    t13 = $[23];
  }
  let t14;
  if ($[24] !== t13) {
    t14 = <Dropdown.Sub>{t12}<Dropdown.Portal><Dropdown.SubContent sideOffset={4}>{t13}</Dropdown.SubContent></Dropdown.Portal></Dropdown.Sub>;
    $[24] = t13;
    $[25] = t14;
  } else {
    t14 = $[25];
  }
  let t15;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <Dropdown.SubTrigger>Composite...</Dropdown.SubTrigger>;
    $[26] = t15;
  } else {
    t15 = $[26];
  }
  let t16;
  if ($[27] !== onChange) {
    t16 = COMPOSITE_MODES.map(mode => <Dropdown.Item key={mode} onSelect={() => onChange("composite", undefined, mode)}>{COMPOSITE_MODE_LABELS[mode]}</Dropdown.Item>);
    $[27] = onChange;
    $[28] = t16;
  } else {
    t16 = $[28];
  }
  let t17;
  if ($[29] !== t16) {
    t17 = <Dropdown.Sub>{t15}<Dropdown.Portal><Dropdown.SubContent sideOffset={4}>{t16}</Dropdown.SubContent></Dropdown.Portal></Dropdown.Sub>;
    $[29] = t16;
    $[30] = t17;
  } else {
    t17 = $[30];
  }
  let t18;
  if ($[31] !== t10 || $[32] !== t14 || $[33] !== t17) {
    t18 = <Dropdown.Portal><Dropdown.Content align="end" sideOffset={4}>{t10}{t11}{t14}{t17}</Dropdown.Content></Dropdown.Portal>;
    $[31] = t10;
    $[32] = t14;
    $[33] = t17;
    $[34] = t18;
  } else {
    t18 = $[34];
  }
  let t19;
  if ($[35] !== t18 || $[36] !== t9) {
    t19 = <Dropdown.Root>{t9}{t18}</Dropdown.Root>;
    $[35] = t18;
    $[36] = t9;
    $[37] = t19;
  } else {
    t19 = $[37];
  }
  return t19;
};
export default TypeSelector;